import { Link } from "react-router-dom";
import { Facebook, Twitter, Instagram, Linkedin, Mail, Phone, MapPin } from "lucide-react";
import Logo from "@/components/layout/Logo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useState } from "react";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async () => {
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/subscribe", { email });
      toast.success("Subscribed! Watch your inbox for updates.");
      setEmail("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not subscribe");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="mt-24 border-t border-border/60 bg-muted/30" data-testid="site-footer">
      <div className="page-shell px-6 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4 space-y-4">
          <Logo />
          <p className="text-sm text-muted-foreground max-w-xs">
            Finzpe is a premium digital lending platform designed for salaried professionals. Transparent, paperless, and thoughtfully engineered.
          </p>
          <div className="flex gap-3 pt-2">
            {[Facebook, Twitter, Instagram, Linkedin].map((Icon, i) => (
              <a key={i} href="#" className="h-9 w-9 rounded-full flex items-center justify-center bg-background border border-border hover:border-primary/60 transition" data-testid={`social-${i}`}>
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-4">Products</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/salary-advance" className="hover:text-primary">Salary Advance</Link></li>
            <li><Link to="/personal-loan" className="hover:text-primary">Personal Loan</Link></li>
            <li><Link to="/eligibility" className="hover:text-primary">Eligibility</Link></li>
            <li><Link to="/emi-calculator" className="hover:text-primary">EMI Calculator</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-4">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-primary">About</Link></li>
            <li><Link to="/careers" className="hover:text-primary">Careers</Link></li>
            <li><Link to="/blog" className="hover:text-primary">Blog</Link></li>
            <li><Link to="/contact" className="hover:text-primary">Contact</Link></li>
            <li><Link to="/grievance" className="hover:text-primary">Grievance</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4 space-y-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Newsletter</div>
          <p className="text-sm text-muted-foreground">Get product updates and personal finance tips. No spam.</p>
          <div className="flex gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@work.com" data-testid="footer-newsletter-email" />
            <Button onClick={subscribe} disabled={loading} className="brand-gradient text-white rounded-full" data-testid="footer-newsletter-submit">Subscribe</Button>
          </div>
          <div className="pt-2 space-y-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2"><Mail className="h-4 w-4" /> hello@finzpe.com</div>
            <div className="flex items-center gap-2"><Phone className="h-4 w-4" /> +91 87000 59442</div>
            <div className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Sector 63, Noida, India</div>
          </div>
        </div>
      </div>

      <div className="border-t border-border/60">
        <div className="page-shell px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} Finzpe Technologies. All rights reserved.</div>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-primary">Privacy</Link>
            <Link to="/terms" className="hover:text-primary">Terms</Link>
            <Link to="/disclaimer" className="hover:text-primary">Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
